/* eslint-disable no-underscore-dangle */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-param-reassign */
/* eslint-disable class-methods-use-this */

interface IQueueEvent {
  eventName?: string;
  eventType: string;
  meta?: Record<string, string | number>;
}

type GlobalWindow = Window &
  typeof globalThis & {
    dataLayer: any[];
  };

const scriptExists = (url: string) => {
  const scripts = document.getElementsByTagName('script');
  for (let i = scripts.length; i--; ) {
    if (scripts[i].src === url) return true;
  }
  return false;
};

export class AnalyticsModel {
  static MAX_POLL_COUNT = 100;

  private _userId = '';
  private eventQueue: IQueueEvent[] = [];

  private gaExecuting = false;
  private gaTimeout = 0;

  public init = () => {
    // add a new init function call for each new analtics service here
    this.initGA(window as GlobalWindow, document, 'script', 'dataLayer', process.env.REACT_APP_GTM_ID!);
  };

  public fireEvent = (event: string, meta?: Record<string, string | number>) => {
    if (event) {
      const evt: IQueueEvent = {
        eventName: event,
        eventType: 'event',
        meta,
      };

      this.eventQueue.push(evt);
    }

    // add new calls for specific analytics platforms here
    this.fireGAEvent();
  };

  public setUserId = (id: string) => {
    if (this._userId !== id) {
      this._userId = id;

      const evt: IQueueEvent = { eventType: 'user' };

      this.eventQueue.push(evt);

      // add new calls for specific analytics platforms here if they too need user data assigned
      this.fireGAEvent();
    }
  };

  /**
   * add new fire event methods for each new analytics platform here
   *
   * each method should include:
   * - an executing flag to prevent multiple calls to the same platform
   * - polling logic to ensure the platform has loaded before attempting to fire events
   * - firing events from this.eventQueue
   * - calling `this.clearQueue()` when all events have been fired
   *
   * DON'T FORGET TO:
   * - set the executing flag to false after this method is done firing all events
   * - add a check for this platforms new executing flag in the `this.clearQueue()`
   *   to ensure that the queue is not cleared if it is still being processed
   */
  private fireGAEvent = (count = 0) => {
    this.gaExecuting = true;
    window.clearTimeout(this.gaTimeout);

    if (count >= AnalyticsModel.MAX_POLL_COUNT) {
      this.gaExecuting = false;
      this.clearQueue();
      return;
    }

    if (!(window as GlobalWindow).dataLayer) {
      this.gaTimeout = window.setTimeout(() => this.fireGAEvent(count + 1), 50);
      return;
    }

    if (!this.eventQueue.length) {
      this.gaExecuting = false;
      return;
    }

    for (const event of this.eventQueue) {
      let evt: Record<string, string | number> = {};
      if (event.eventType === 'event') {
        evt = {
          event: event.eventName!,
        };
      }

      if (event.eventType === 'user') {
        evt = {
          user_id: this._userId,
        };
      }

      let meta: Record<string, string | number> = {};

      if (event.meta) meta = { ...event.meta, ...meta };

      evt = { ...evt, ...meta };

      (window as GlobalWindow).dataLayer.push(evt);
    }

    this.gaExecuting = false;
    this.clearQueue();
  };

  /**
   * add new init methods for each new analytics platform here
   */
  private initGA = (w: any, d: any, s: string, l: string, i: string) => {
    w.dataLayer = (window as GlobalWindow).dataLayer || [];
    w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const dl = l !== 'dataLayer' ? `&l=${l}` : '';
    const scr = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;

    if (!scriptExists(scr)) {
      const f = d.getElementsByTagName(s)[0];
      const j: HTMLScriptElement = d.createElement('script');
      j.async = true;
      j.src = scr;
      f?.parentNode?.insertBefore(j, f);
    }
  };

  private clearQueue = () => {
    if (this.gaExecuting) return;
    this.eventQueue = [];
  };
}
